import React from "react";
import { Link } from "react-router-dom";
import { Trophy, Radio } from "lucide-react";
import { socials, kickChannel, formatMoney, CODE } from "../mock";
import { useLeaderboard } from "../hooks/useLeaderboard";
import { useKickStatus } from "../hooks/useKickStatus";

/* ---------- Leaderboard teaser (Duelbits only) ---------- */
export const LeaderboardTeaser = () => {
  const { data, loading } = useLeaderboard();
  return (
  <section className="relative py-24 bg-[#12151f] overflow-hidden" data-testid="leaderboard-teaser">
    <div className="absolute top-[-30%] left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full opacity-[0.08] blur-[110px] bg-[#3ddc84] pointer-events-none" />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative">
      <h2 className="font-display font-extrabold italic text-4xl sm:text-6xl uppercase tracking-tight text-white">
        Leader<span className="text-[#3ddc84]">board</span>
      </h2>
      <p className="mt-4 text-[#97a1b8] max-w-md mx-auto text-sm sm:text-base">
        Wager on Duelbits under code{" "}
        <span className="text-[#3ddc84] font-bold">“{CODE}”</span> and earn insane money
        prizes every month!
      </p>

      <div className="mt-9 inline-flex flex-col items-center">
        <img src="/duelbits-logo.png" alt="Duelbits" className="h-9 w-auto object-contain" />
        <p className="mt-5 uppercase text-[10px] font-bold tracking-[0.25em] text-[#7e879c]">
          Monthly prize pool
        </p>
        <p className={`glow-text mt-1 font-display font-extrabold italic text-5xl ${loading ? "animate-pulse" : ""}`}>
          {loading ? "…" : data?.prizePool != null ? formatMoney(data.prizePool) : "—"}
        </p>
      </div>

      <div className="mt-9">
        <Link
          to="/leaderboard"
          className="btn-duel inline-flex items-center gap-2.5 px-8 py-3.5 text-sm"
          data-testid="view-leaderboards-button"
        >
          <Trophy size={17} strokeWidth={2.5} />
          View Leaderboard
        </Link>
      </div>
    </div>
  </section>
  );
};

/* ---------- Kick stream section (replaces videos) ---------- */
// Live status comes from the backend (/api/kick/status), which polls the
// official Kick API with app credentials. Badge refreshes every 60s.
export const KickSection = () => {
  const { isLive, viewers } = useKickStatus();
  const live = isLive;
  return (
    <section className="py-24 bg-[#171b26]" data-testid="kick-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <h2 className="font-display font-extrabold italic text-4xl sm:text-5xl uppercase tracking-tight text-white">
              Live <span className="text-[#3ddc84]">Stream</span>
            </h2>
            <p className="mt-2 uppercase text-xs font-bold tracking-[0.25em] text-[#7e879c]">
              kick.com/{kickChannel.slug}
            </p>
          </div>
          <a
            href={kickChannel.url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-slate inline-flex items-center gap-2 px-6 py-3 text-[12px]"
            data-testid="kick-watch-button"
          >
            <Radio size={15} />
            Watch on Kick
          </a>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-[#2c3448] bg-[#12151f] shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
          {/* status badge */}
          <div
            className={`absolute top-4 left-4 z-10 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-widest backdrop-blur-md ${
              live
                ? "bg-[#3ddc84]/90 text-[#0d1420]"
                : "bg-[#12151f]/85 text-[#97a1b8] border border-[#2c3448]"
            }`}
            data-testid="kick-status-badge"
          >
            <span
              className={`w-2 h-2 rounded-full ${
                live ? "bg-[#0d1420] animate-pulse" : "bg-[#5d6579]"
              }`}
            />
            {live ? `Live${viewers ? ` · ${viewers} watching` : ""}` : "Offline"}
          </div>

          <div className="aspect-video">
            <iframe
              src={kickChannel.embed}
              title="WAXUM on Kick"
              className="w-full h-full"
              frameBorder="0"
              scrolling="no"
              allowFullScreen
              allow="autoplay; fullscreen; encrypted-media"
              data-testid="kick-embed"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Socials ---------- */
const XIcon = ({ size = 26, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const KickIcon = ({ size = 26, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M4 2h6v6h2V6h2V4h2V2h4v6h-2v2h-2v2h2v2h2v6h-4v-2h-2v-2h-2v2H10v6H4V2z" transform="scale(0.92) translate(1,1)" />
  </svg>
);

const DiscordIcon = ({ size = 26, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.058a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.182 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
);

const iconFor = (id) =>
  id === "twitter" ? <XIcon size={24} /> : id === "discord" ? <DiscordIcon size={24} /> : <KickIcon size={24} />;

export const SocialsSection = () => (
  <section className="relative py-24 bg-[#12151f] overflow-hidden" data-testid="socials-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6">
      <h2 className="text-center font-display font-extrabold italic text-4xl sm:text-5xl uppercase tracking-tight text-white mb-14">
        Follow <span className="text-[#3ddc84]">WAXUM</span> Socials
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {socials.map((s) => (
          <a
            key={s.id}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex flex-col items-center text-center p-8 rounded-2xl bg-[#1d2231] border border-[#2c3448] hover:border-[#3ddc84]/45 hover:-translate-y-1.5 transition-all duration-300"
            data-testid={`social-card-${s.id}`}
          >
            <div className="w-16 h-16 rounded-2xl bg-[#3ddc84]/10 border border-[#3ddc84]/25 flex items-center justify-center text-[#3ddc84] group-hover:shadow-[0_0_28px_rgba(61,220,132,0.35)] group-hover:scale-110 transition-all duration-300">
              {iconFor(s.id)}
            </div>
            <p className="mt-5 font-bold text-white text-lg">{s.name}</p>
            <p className="mt-1 text-[12px] font-semibold text-[#3ddc84]">{s.handle}</p>
            <div className="w-10 h-px bg-[#2c3448] my-3" />
            <p className="text-[13px] text-[#97a1b8]">{s.sub}</p>
            <span className="btn-slate mt-6 px-7 py-2.5 text-[12px] inline-flex">{s.action}</span>
          </a>
        ))}
      </div>
    </div>
  </section>
);
